import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SelectedFilters,
  Summary,
  Tag,
  SelectedItem,
} from "../../models/interfaces";
import { SelectedDocumentType } from "../../models/enums";
import { SearchMode, SearchStep } from "../../models/interfaces";
import { initialSelectedFilters } from "../../models/consts";
import { searchModeFromUrl } from "../../utils/searchDepthLink";
import { any } from "bluebird";

export const initialState = {
  lastQ: "",
  exactMatch: "",
  lastQResults: null as any,
  lastSelectedFilters: initialSelectedFilters,
  lastExactMatch: "",
  kapps: [] as any[],
  summary: null as Summary | null,
  answerLanguage: "",
  // Phase message shown in place of the loader while the answer is still forming.
  summaryStatus: "" as string,
  // Live progress steps for the current query, in arrival order (issue 1386). The
  // backend streams these as [STEP] events; medium and high mode do real work before
  // the first answer token and this is what the user watches instead of a spinner.
  searchSteps: [] as SearchStep[],
  // The mode the visitor picked. "" means "whatever the portal defaults to" — the
  // client then sends no search_mode param and learns the answer from [SEARCH_MODE].
  //
  // Starts at a shared link's depth, not "": on edge the results route sends its
  // first request on mount, from a render that predates any effect, so a depth
  // hydrated by an effect would miss it.
  searchMode: (searchModeFromUrl() || "") as SearchMode | "",
  // The mode the backend reported for the last query, used to label the picker while
  // the visitor has not overridden it.
  resolvedSearchMode: "" as SearchMode | "",
  // The mode the answer on screen was produced with. Part of "is this the same
  // search?" (isSearchAgain), so submitting an unchanged question at a new depth is a
  // real search rather than a no-op.
  lastSearchMode: "" as SearchMode | "",
  // Bumped every time the visitor submits the search field — Enter or the send
  // button. The results route watches it, which is what makes a submit the only thing
  // that starts a search: picking a depth writes `searchMode` and nothing else
  // happens until the next submit (issue 1563).
  searchSubmitId: 0,
  // Bumped every time the route actually sends a search. The search boxes watch it and
  // put their text back to the question being searched, so a draft nobody submitted
  // cannot label someone else's results — the filter half of issue 1563.
  queryRunId: 0,
  // True from the moment the route sends a search until the stream ends — by
  // [DONE], by an error, or because the visitor stopped it. The search boxes watch
  // it to show Stop in place of Send; they live outside the results route, which
  // is why this is in the store rather than that route's `loading` state.
  isSearching: false,
  // Identifies the search turn in flight. Minted per submitted query, sent to
  // stream_gpt as `search_id`, and the handle `POST /ai/client/portal/stop` needs
  // to stop it server-side. Also stamped on every analytics event the turn
  // produces, which is what ties a visitor's actions to the query behind them.
  searchId: "" as string,
  // The id a search box minted for the query it is about to navigate to, waiting
  // for the results route to pick it up. The box has to know the id at submit
  // time — its own `searchSubmit` event carries it — but the route is what starts
  // the run, and one turn must not be two ids. Empty when the run was started by
  // something other than a submit (a filter change re-running the same question),
  // which then mints its own.
  pendingSearchId: "" as string,
  // Object URL of the image attached to an image+text search, shown as a
  // thumbnail on the results page while the search is processing. Empty when the
  // current search has no attached image. The creator (results page) revokes it.
  imagePreview: "" as string,
  tags: [] as Tag[],
  selectedFilters: initialSelectedFilters,
  isChronological: false,
  chronologicalOrder: false,
  searchResltsBackup: [] as any[],
  typedYearFrom: "",
  typedYearTo: "",
  yearFilterStatus: false,
  docsFilterStatus: false,
  categories: [] as any,
  selectedCategory: [] as SelectedItem[],
  inputValue: "",
  keywordsDocuments: {
    pages: 1,
    source: [] as any,
  },
  initialKeywordsDocuments: {
    pages: 1,
    source: [] as any,
  },
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setKeywordsDocuments: (state, action: PayloadAction<any>) => {
      state.keywordsDocuments = action.payload;
    },
    setInitialKeywordsDocuments: (state, action: PayloadAction<any>) => {
      state.initialKeywordsDocuments = action.payload;
    },
    setInputValue: (state, action: PayloadAction<any>) => {
      state.inputValue = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<any>) => {
      state.selectedCategory = action.payload;
    },
    setCategories: (state, action: PayloadAction<any>) => {
      state.categories = action.payload;
    },
    setLastQ: (state, action: PayloadAction<string>) => {
      state.lastQ = action.payload;
    },
    setExactMatch: (state, action: PayloadAction<string>) => {
      state.exactMatch = action.payload;
    },
    setLastExactMatch: (state, action: PayloadAction<string>) => {
      state.lastExactMatch = action.payload;
    },
    setLastQResults: (state, action: PayloadAction<any>) => {
      state.lastQResults = action.payload;
    },
    setKapps: (state, action: PayloadAction<any[]>) => {
      state.kapps = action.payload;
    },
    setSummary: (state, action: PayloadAction<Summary | null>) => {
      state.summary = action.payload;
      if (!action.payload) state.answerLanguage = "";
    },
    setAnswerLanguage: (state, action: PayloadAction<string>) => {
      state.answerLanguage = action.payload;
    },
    setSummaryText: (state, action: PayloadAction<string>) => {
      if (state.summary) {
        state.summary.text = action.payload;
      }
    },
    setSummaryStyle: (state, action: PayloadAction<string>) => {
      if (state.summary) {
        state.summary.style = action.payload;
      }
    },
    setSummaryStatus: (state, action: PayloadAction<string>) => {
      state.summaryStatus = action.payload;
    },
    // Steps arrive as (running -> done) pairs keyed by `key`, so an update replaces
    // the row it resolves rather than appending a second one.
    upsertSearchStep: (state, action: PayloadAction<SearchStep>) => {
      const index = state.searchSteps.findIndex(
        (step) => step.key === action.payload.key,
      );
      if (index === -1) {
        state.searchSteps.push(action.payload);
      } else {
        state.searchSteps[index] = action.payload;
      }
    },
    clearSearchSteps: (state) => {
      state.searchSteps = [];
    },
    /** The depth to search at: chosen by the visitor, or read back from what this
     *  portal remembered. `""` is Auto — send no search_mode param and let the portal
     *  decide — which is also what a portal with nothing stored restores to.
     *
     *  Setting it starts nothing; the depth applies to the next question the visitor
     *  submits (issue 1563). A pick and a restore were briefly separate actions, back
     *  when the search route re-ran the query on the former and had to not on the
     *  latter; with no depth change running a query there is nothing left to tell
     *  apart, and the picker persists a pick at the point it makes one. */
    setSearchMode: (state, action: PayloadAction<SearchMode | "">) => {
      state.searchMode = action.payload;
    },
    setResolvedSearchMode: (state, action: PayloadAction<SearchMode | "">) => {
      state.resolvedSearchMode = action.payload;
    },
    setLastSearchMode: (state, action: PayloadAction<SearchMode | "">) => {
      state.lastSearchMode = action.payload;
    },
    /** A search box submitted. `payload` is the id it minted for the query, if it
     *  minted one; anything that submits without an id (a suggested search) leaves
     *  it empty and the route mints instead. Always assigned, so a submit cannot
     *  inherit the previous submit's pending id. */
    markSearchSubmitted: (state, action: PayloadAction<string | undefined>) => {
      state.searchSubmitId += 1;
      state.pendingSearchId = action.payload || "";
    },
    markQueryRun: (state) => {
      state.queryRunId += 1;
    },
    /** Starts a search turn: a fresh id, and the boxes switch to Stop. Consumes
     *  the pending id so the next run that is not a submit mints its own. */
    startSearchRun: (state, action: PayloadAction<string>) => {
      state.searchId = action.payload;
      state.pendingSearchId = "";
      state.isSearching = true;
      // The resolved depth belongs to the previous turn until this one's
      // [SEARCH_MODE] frame lands; an event stamped in between must not carry
      // it under the new id.
      state.resolvedSearchMode = "";
    },
    /** The stream ended — finished, failed, or stopped. The id stays: actions
     *  taken on the answer afterwards still belong to this turn. */
    endSearchRun: (state) => {
      state.isSearching = false;
    },
    /** The visitor pressed Stop. Marks the answer on screen as final so the card
     *  stops waiting for text that is not coming; `payload` is the message to show
     *  when none had arrived at all. Creates the summary when the stop lands before
     *  one exists, which is exactly the case that used to leave a dead loader. */
    markSummaryStopped: (state, action: PayloadAction<string>) => {
      if (state.summary) {
        state.summary.stopped = true;
        if (!state.summary.text) state.summary.text = action.payload;
      } else {
        state.summary = {
          text: action.payload,
          sources: [],
          style: "Free Text",
          type: "gpt",
          error: "",
          resultDone: true,
          stopped: true,
        };
      }
    },
    setImagePreview: (state, action: PayloadAction<string>) => {
      state.imagePreview = action.payload;
    },
    setTags: (state, action: PayloadAction<Tag[]>) => {
      state.tags = action.payload;
    },
    setSelectedFilters: (state, action: PayloadAction<SelectedFilters>) => {
      state.selectedFilters = action.payload;
    },
    setLastSelectedFilters: (state, action: PayloadAction<SelectedFilters>) => {
      state.lastSelectedFilters = action.payload;
    },
    setIsChronological: (state, action: PayloadAction<boolean>) => {
      state.isChronological = action.payload;
    },
    setChronologicalOrder: (state, action: PayloadAction<boolean>) => {
      state.chronologicalOrder = action.payload;
    },
    setSearchResltsBackup: (state, action: PayloadAction<any[]>) => {
      state.searchResltsBackup = action.payload;
    },
    setTypedYearFrom: (state, action: PayloadAction<string>) => {
      state.typedYearFrom = action.payload;
    },
    setTypedYearTo: (state, action: PayloadAction<string>) => {
      state.typedYearTo = action.payload;
    },
    setYearFilterStatus: (state, action: PayloadAction<boolean>) => {
      state.yearFilterStatus = action.payload;
    },
    setDocsFilterStatus: (state, action: PayloadAction<boolean>) => {
      state.docsFilterStatus = action.payload;
    },
  },
});

export const {
  setLastQ,
  setExactMatch,
  setLastQResults,
  setKapps,
  setSummary,
  setSummaryText,
  setAnswerLanguage,
  setSummaryStyle,
  setSummaryStatus,
  upsertSearchStep,
  clearSearchSteps,
  setSearchMode,
  setResolvedSearchMode,
  setLastSearchMode,
  markSearchSubmitted,
  markQueryRun,
  startSearchRun,
  endSearchRun,
  markSummaryStopped,
  setImagePreview,
  setTags,
  setSelectedFilters,
  setLastSelectedFilters,
  setIsChronological,
  setChronologicalOrder,
  setSearchResltsBackup,
  setTypedYearFrom,
  setTypedYearTo,
  setYearFilterStatus,
  setDocsFilterStatus,
  setLastExactMatch,
  setCategories,
  setSelectedCategory,
  setInputValue,
  setKeywordsDocuments,
  setInitialKeywordsDocuments,
} = searchSlice.actions;
export default searchSlice.reducer;
